import { useEffect } from 'react';
import { SITE_NAME, absoluteUrl } from '../utils/seo';

interface SeoOptions {
  title: string;
  description: string;
  path: string;
  noindex?: boolean;
  ogType?: 'website' | 'article';
  jsonLd?: Record<string, unknown>[];
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}

export function useSeo({ title, description, path, noindex = false, ogType = 'website', jsonLd = [] }: SeoOptions) {
  const ldString = JSON.stringify({ '@context': 'https://schema.org', '@graph': jsonLd });

  useEffect(() => {
    const url = absoluteUrl(path);
    document.title = title;
    document.documentElement.lang = 'nl-NZ';

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1');
    upsertMeta('name', 'geo.region', 'NZ');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', 'nl_NZ');
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: url });
    upsertLink('link[rel="alternate"][hreflang="nl-NZ"]', { rel: 'alternate', hreflang: 'nl-NZ', href: url });
    upsertLink('link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default', href: url });

    let script = document.getElementById('seo-jsonld') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = 'seo-jsonld';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = ldString;
  }, [title, description, path, noindex, ogType, ldString]);
}
